import {
  combineLatestTyped,
  downloadFile,
  filterByLatest,
  filterNotUndefined,
  filterValue,
  IList,
  useBooleanState,
  useDataStream,
  useEventAsStream,
  useStream,
  useStreamEffect,
  useStreamValue,
  useValueAsStream,
  useVoidEventAsStream
} from 'annotation-tools/utils';
import React, { FC, memo, useMemo } from 'react';
import { map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  getDownloadLink,
  getProjectSummary
} from '../../../api/fetch-functions/project';
import { approveProject } from '../../../api/fetch-functions/project/approve';
import { unapproveProject } from '../../../api/fetch-functions/project/unapprove';
import { viewTexts } from '../../../constants/view-texts';
import { envMode } from '../../../env-values';
import { token$ } from '../../../global-state/auth-state';
import { getListItem } from '../../../global-state/database/common/get-list-item';
import {
  ListItemWithPromiseState,
  listItemWithPromiseStateInit
} from '../../../global-state/database/common/list-item-with-status';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../../global-state/database/common/list-with-status';
import { mapListItemLatest } from '../../../global-state/database/common/map-latest';
import { attachPromiseStateToList } from '../../../global-state/database/common/rxjs-operator/attach-promise-state-to-list';
import {
  fetchProjectListOf,
  projectListOf
} from '../../../global-state/database/project-list';
import { companyId$, projectId$ } from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { IProjectImageInfoType } from '../../../types/record/image-info';
import { IProjectType } from '../../../types/record/project';
import { IProjectSummaryType } from '../../../types/record/project-summary';
import { ConfirmProjectResultDialog } from '../../dialogs/approve-project-dialog/approve-project-dialog';
import { ConfirmUnapproveProjectDialog } from '../../dialogs/unapprove-project-dialog/unapprove-project-dialog';
import { ProjectDetailView } from './project-detail-view';

interface Props {
  imageList: IList<IProjectImageInfoType>;
  onEditProjectImagesClick: () => void;
}

export const ProjectDetail: FC<Props> = memo(
  ({ imageList, onEditProjectImagesClick }: Readonly<Props>) => {
    /* event handlers */

    const [downloadPdfClick$, onDownloadPdfClick] = useVoidEventAsStream();
    const [downloadCsvClick$, onDownloadCsvClick] = useVoidEventAsStream();

    const [
      confirmApproveDialogClose$,
      onConfirmApproveDialogClose
    ] = useEventAsStream<'yes' | 'no'>();

    const [
      confirmApproveDialogIsOpen,
      onApproveResultClick,
      closeConfirmApproveDialog
    ] = useBooleanState(false);

    useStreamEffect(
      confirmApproveDialogClose$.pipe(filterValue('no')),
      closeConfirmApproveDialog
    );

    const [
      confirmUnapproveDialogClose$,
      onConfirmUnapproveDialogClose
    ] = useEventAsStream<'yes' | 'no'>();

    const [
      confirmUnapproveDialogIsOpen,
      onUnapproveClick,
      closeConfirmUnapproveDialog
    ] = useBooleanState(false);

    useStreamEffect(
      confirmUnapproveDialogClose$.pipe(filterValue('no')),
      closeConfirmUnapproveDialog
    );

    /* streams */

    const projectList$ = useDataStream<ListWithPromiseState<IProjectType>>(
      listWithPromiseStateInit<IProjectType>(),
      companyId$.pipe(switchMap(projectListOf))
    );

    const project$ = useDataStream<ListItemWithPromiseState<IProjectType>>(
      listItemWithPromiseStateInit<IProjectType>(),
      combineLatestTyped(projectList$, projectId$).pipe(
        map(([list, id]) => getListItem(list, id))
      )
    );

    const projectIsApproved$ = useDataStream<ListItemWithPromiseState<boolean>>(
      listItemWithPromiseStateInit(),
      project$.pipe(
        map(p => mapListItemLatest(p, p => p.status === 'approved'))
      )
    );

    const projectName$ = useDataStream<ListItemWithPromiseState<string>>(
      listItemWithPromiseStateInit(),
      project$.pipe(map(p => mapListItemLatest(p, p => p.name)))
    );

    const projectAuthorName$ = useDataStream<ListItemWithPromiseState<string>>(
      listItemWithPromiseStateInit(),
      project$.pipe(map(p => mapListItemLatest(p, p => p.author)))
    );

    const projectSummary$ = useDataStream<
      ListWithPromiseState<IProjectSummaryType>
    >(
      listWithPromiseStateInit(),
      combineLatestTyped(token$.pipe(filterNotUndefined()), projectId$).pipe(
        map(([token, projectId]) => getProjectSummary(token)(projectId)),
        attachPromiseStateToList('switchMap')
      )
    );

    const numCounted = useMemo<number>(
      () => imageList.count(e => e.isAnnotated),
      [imageList]
    );

    const projectImagesSummaryDescription = useMemo<string>(
      () =>
        viewTexts.pages.projectDetail.projectImageDataSummary({
          numImages: imageList.size,
          numCounted
        }),
      [imageList.size, numCounted]
    );

    const projectIsApprovable = useMemo<boolean>(
      () => !imageList.isEmpty(),
      [imageList]
    );

    /* event side effect streams */

    const projectIsApprovable$ = useValueAsStream(projectIsApprovable);

    const approveResponse$ = useStream(
      confirmApproveDialogClose$.pipe(
        filterValue('yes'),
        filterByLatest(projectIsApprovable$),
        withLatestFrom(token$.pipe(filterNotUndefined()), projectId$),
        mergeMap(([_, token, projectId]) =>
          approveProject(token)(projectId)({}).then(res => {
            if (res.type === 'error') {
              if (envMode !== 'production') {
                // TODO: error handling
                console.log('error', res.body);
              }
            }
            return res.type;
          })
        )
      )
    );

    const unapproveResponce$ = useStream(
      confirmUnapproveDialogClose$.pipe(
        filterValue('yes'),
        withLatestFrom(token$.pipe(filterNotUndefined()), projectId$),
        mergeMap(([_, token, projectId]) =>
          unapproveProject(token)(projectId)({}).then(res => {
            if (res.type === 'error') {
              if (envMode !== 'production') {
                // TODO: error handling
                console.log('error', res.body);
              }
            }
            return res.type;
          })
        )
      )
    );

    /* extract values */

    const projectName = useStreamValue(projectName$);
    const projectAuthorName = useStreamValue(projectAuthorName$);
    const projectIsApproved = useStreamValue(projectIsApproved$);
    const projectSummary = useStreamValue(projectSummary$);

    /* side effects */

    useStreamEffect(
      downloadPdfClick$.pipe(
        withLatestFrom(token$.pipe(filterNotUndefined()), projectId$),
        switchMap(([_, token, projectId]) =>
          getDownloadLink(token)(projectId, 'PDF')
        )
      ),
      res => {
        if (res.type === 'error') {
          console.log(res.body); // TODO: error handling
          return;
        }
        downloadFile(res.body.url);
      }
    );

    useStreamEffect(
      downloadCsvClick$.pipe(
        withLatestFrom(token$.pipe(filterNotUndefined()), projectId$),
        switchMap(([_, token, projectId]) =>
          getDownloadLink(token)(projectId, 'CSV')
        )
      ),
      res => {
        if (res.type === 'error') {
          console.log(res.body); // TODO: error handling
          return;
        }
        downloadFile(res.body.url);
      }
    );

    useStreamEffect(
      approveResponse$.pipe(withLatestFrom(companyId$)),
      ([res, companyId]) => {
        if (res === 'success') {
          fetchProjectListOf(companyId);
          closeConfirmApproveDialog();
          // TODO: fetch project with projectId instead of entire list
          // TODO: 読み込み中画面を挟むようにする
        }
      }
    );

    useStreamEffect(
      unapproveResponce$.pipe(withLatestFrom(companyId$)),
      ([res, companyId]) => {
        if (res === 'success') {
          fetchProjectListOf(companyId);
          closeConfirmUnapproveDialog();
          // TODO: fetch project with projectId instead of entire list
          // TODO: 読み込み中画面を挟むようにする
        }
      }
    );

    useLoadingPage(
      projectIsApproved?.status === 'pending' ||
        projectName?.status === 'pending' ||
        projectAuthorName?.status === 'pending' ||
        projectSummary?.status === 'pending'
    );

    return (
      <>
        {projectIsApproved?.value.latest === undefined ||
        projectName?.value.latest === undefined ||
        projectAuthorName?.value.latest === undefined ||
        projectSummary?.value.latest === undefined ? (
          undefined
        ) : (
          <>
            <ProjectDetailView
              projectAuthorName={projectAuthorName.value.latest}
              projectIsApproved={projectIsApproved.value.latest}
              projectSummary={projectSummary.value.latest}
              noImageRegistered={imageList.isEmpty()}
              projectIsApprovable={projectIsApprovable}
              projectImagesSummaryDescription={projectImagesSummaryDescription}
              onEditProjectImagesClick={onEditProjectImagesClick}
              onUnapproveClick={onUnapproveClick}
              onApproveResultClick={onApproveResultClick}
              onDownloadPdfClick={onDownloadPdfClick}
              onDownloadCsvFileClick={onDownloadCsvClick}
            />
            <ConfirmProjectResultDialog
              projectName={projectName.value.latest}
              projectSummary={projectSummary.value.latest}
              open={confirmApproveDialogIsOpen}
              onClose={onConfirmApproveDialogClose}
            />
            <ConfirmUnapproveProjectDialog
              projectName={projectName.value.latest}
              open={confirmUnapproveDialogIsOpen}
              onClose={onConfirmUnapproveDialogClose}
            />
          </>
        )}
      </>
    );
  }
);

ProjectDetail.displayName = 'ProjectDetail';
